import React, { useState, useEffect, useCallback } from 'react';
import { Download, RefreshCw, AlertCircle, TrendingUp } from 'lucide-react';
import TimePeriodSelector from './TimePeriodSelector';
import SpendingSummaryCards from './SpendingSummaryCards';
import BudgetUtilizationCard from './BudgetUtilizationCard';
import SpendingTrendChart from './SpendingTrendChart';
import TLDDistributionChart from './TLDDistributionChart';
import { apiService } from '../../services/api';
import type {
  TimePeriod,
  SpendingAnalyticsSummary,
  SpendingTrendData,
  TLDDistributionData,
  SpendingAnalyticsDashboardProps,
} from '../../types/analytics';

/**
 * SpendingAnalyticsDashboard Component
 *
 * Main container component that orchestrates all analytics sub-components.
 * Fetches data from the analytics API endpoints and passes to child components.
 *
 * Features:
 * - Time period selection (presets + custom date range)
 * - Summary metrics cards
 * - Budget utilization indicators (daily/monthly)
 * - Spending trend chart (line chart)
 * - TLD distribution chart (pie/bar chart)
 * - CSV export functionality
 */
const SpendingAnalyticsDashboard: React.FC<SpendingAnalyticsDashboardProps> = ({
  projectId,
}) => {
  // State for time period selection
  const [selectedPeriod, setSelectedPeriod] = useState<TimePeriod>('this_month');
  const [customStartDate, setCustomStartDate] = useState<string | undefined>();
  const [customEndDate, setCustomEndDate] = useState<string | undefined>();

  // State for analytics data
  const [summary, setSummary] = useState<SpendingAnalyticsSummary | null>(null);
  const [trendData, setTrendData] = useState<SpendingTrendData | null>(null);
  const [tldData, setTldData] = useState<TLDDistributionData | null>(null);

  // State for loading/error
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  /**
   * Fetch all analytics data
   */
  const fetchAnalyticsData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all data in parallel
      const [summaryResult, trendsResult, tldResult] = await Promise.all([
        apiService.getSpendingAnalyticsSummary(
          projectId,
          selectedPeriod,
          customStartDate,
          customEndDate
        ),
        apiService.getSpendingTrends(
          projectId,
          selectedPeriod,
          undefined, // auto-select granularity
          customStartDate,
          customEndDate
        ),
        apiService.getTLDDistribution(
          projectId,
          selectedPeriod,
          10, // top 10 TLDs
          customStartDate,
          customEndDate
        ),
      ]);

      setSummary(summaryResult);
      setTrendData(trendsResult);
      setTldData(tldResult);
    } catch (err: any) {
      console.error('Failed to fetch analytics:', err);
      // Check for 401 status in axios error response format
      if (err.status === 401 || err.response?.status === 401) {
        setError('Please log in to view analytics data.');
      } else {
        setError('Failed to load analytics data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [projectId, selectedPeriod, customStartDate, customEndDate]);

  // Fetch data on mount and when dependencies change
  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  /**
   * Handle period change from TimePeriodSelector
   */
  const handlePeriodChange = (
    period: TimePeriod,
    start?: string,
    end?: string
  ) => {
    setSelectedPeriod(period);
    setCustomStartDate(start);
    setCustomEndDate(end);
  };

  /**
   * Handle CSV export
   */
  const handleExport = async () => {
    setExporting(true);
    setExportError(null);

    try {
      const blob = await apiService.exportSpendingAnalytics(
        projectId,
        customStartDate,
        customEndDate
      );

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `spending-analytics-${projectId}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed:', err);
      setExportError('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  /**
   * Handle trend chart data point click
   */
  const handleTrendDataPointClick = (date: string) => {
    // Could filter to show details for that specific date
    console.log('Trend data point clicked:', date);
  };

  /**
   * Handle TLD chart segment click
   */
  const handleTLDClick = (tld: string) => {
    // Could filter to show domains with that TLD
    console.log('TLD clicked:', tld);
  };

  // Check if there's no data at all
  const hasNoData = summary &&
    summary.total_purchases === 0 &&
    trendData?.data_points.length === 0 &&
    tldData?.distributions.length === 0;

  // Loading state
  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-6">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading analytics...</span>
        </div>
        {/* Skeleton loading state */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-64 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
          <div className="h-64 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse" />
          <div className="h-96 bg-gray-100 dark:bg-gray-700 rounded-lg animate-pulse col-span-full" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-red-700 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={fetchAnalyticsData}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="spending-analytics-dashboard" className="p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-warm-white">Spending Analytics</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Track your domain purchasing patterns and budget utilization
          </p>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
        </div>
      </div>

      {/* Export error message */}
      {exportError && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md text-red-700 dark:text-red-400 text-sm">
          {exportError}
        </div>
      )}

      {/* Time Period Selector */}
      <div className="mb-6">
        <TimePeriodSelector
          selectedPeriod={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          onChange={handlePeriodChange}
        />
      </div>

      {/* Empty state */}
      {hasNoData && (
        <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-8 text-center">
          <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-2">
            No purchase data available
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Start purchasing domains to see your spending analytics here.
          </p>
        </div>
      )}

      {/* Dashboard grid */}
      {!hasNoData && (
        <div className="grid grid-cols-1 gap-6">
          {/* Summary Cards */}
          <SpendingSummaryCards
            summary={summary}
            loading={false}
            error={null}
          />

          {/* Budget Utilization Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BudgetUtilizationCard
              label="Daily Budget"
              currentSpent={summary?.daily_spent || 0}
              budgetLimit={summary?.daily_budget || null}
              period="daily"
            />
            <BudgetUtilizationCard
              label="Monthly Budget"
              currentSpent={summary?.monthly_spent || 0}
              budgetLimit={summary?.monthly_budget || null}
              period="monthly"
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <SpendingTrendChart
              data={trendData}
              loading={false}
              onDataPointClick={handleTrendDataPointClick}
            />
            <TLDDistributionChart
              data={tldData}
              loading={false}
              chartType="pie"
              onTLDClick={handleTLDClick}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default SpendingAnalyticsDashboard;
